<template lang="pug">
    v-card(outlined)

        .text-center(v-if="isLoading")
            v-progress-circular(
                :size='100'
                :width='10'
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 75px; margin-bottom: 75px;"
            )

        v-card-text(v-else)
            div(style="display: flex; flex-direction: column;")
                slot(name="scorecard-items")
                    template(v-if="validScorecardItems")
                        template(v-for="scorecardItem in scorecardItems")
                            div.mb-6(style="display: flex; flex-direction: column;")
                                .mb-7
                                    span.text-medium {{ scorecardItem.item_order + '. ' }}
                                    span.text-medium {{ scorecardItem.name }} 
                                .pa-2
                                    ScorecardItemSlider(
                                        :type="scorecardItem.item_type"
                                        :answer="scorecardItem.answer"
                                        :readonly="readonly"
                                        @change="(answer) => updateScorecardItemAnswer(scorecardItem, answer)"
                                    )

                                ScorecardItemNotes(
                                    :item="scorecardItem"
                                    label="Item Notes"
                                    :readonly="readonly"
                                    :saving="scorecardItem.is_saving_note"
                                    :show-private-notes="showPrivateNotes"
                                    :show-snackbar="showSnackbar"
                                    @change="(options) => updateScorecardItemNotes(scorecardItem, options)"
                                )

                            v-divider.mx-0.mt-0

                    template(v-else-if="scorecard.scorecard_template_id === 0")
                        v-alert.mb-0(outlined type="info" dense border='left') 
                            | Add a template to manually score items.

                    template(v-else)
                        v-alert.mb-0(outlined type="error" dense border='left') 
                            | Unable to find scorecard items.

                ScorecardItemNotes.mt-4(
                    v-if="validScorecard"
                    :item="scorecard"
                    label="Notes"
                    :readonly="readonly"
                    :saving="isSavingScorecardNote"
                    :show-private-notes="showPrivateNotes"
                    :show-snackbar="showSnackbar"
                    @change="(options) => updateScorecardNotes(options)"
                )

</template>

<script lang="ts">
    import Vue from 'vue';

    // Components
    import ScorecardItemSlider from '@/components/shared/scorecard_item_slider.vue';
    import ScorecardItemNotes from '@/components/scorecards/note.vue'
    import ScorecardNotes from '@/components/scorecards/note.vue'

    // Data
    import {
        GetScorecardItems,
        UpdateScorecard,
        UpdateScorecardItemAnswer,
        UpdateScorecardItemNotes,
    } from '@/data';

    export default Vue.extend({
        data() {
            return {
                isLoading: false,
                isSavingScorecardNote: false,
                notes: null,
                scorecardItems: [],
            }
        },
        methods: {
            getScorecardItems() {
                if (!this.validScorecard) {
                    return;
                }

                this.isLoading = true;
                GetScorecardItems(this.scorecard.scorecard_id, {}).then((response) => {
                    if (response?.Data?.scorecard_items?.length > 0) {
                        this.scorecardItems = response.Data.scorecard_items;
                        this.notes = response.Data.scorecard_notes || null;

                        this.setScorecardCompletion();
                    }

                    this.isLoading = false;
                });
            },
            handleIsSavingScorecardItemNote(scorecardItem, isSaving) {
                if (!(scorecardItem?.scorecard_item_id > 0)) {
                    return;
                }

                scorecardItem.is_saving_note = isSaving;
                this.handleUpdateScorecardItem(scorecardItem);
            },
            handleUpdateScorecardItem(scorecardItem) {
                if (!(scorecardItem?.scorecard_item_id > 0)) {
                    return;
                }

                const idxToUpdate = this.scorecardItems.findIndex(x => x.scorecard_item_id === scorecardItem.scorecard_item_id);

                if (idxToUpdate >= 0) {
                    this.scorecardItems.splice(idxToUpdate, 1, scorecardItem);
                }

                this.setScorecardCompletion();
            },
            init() {
                this.getScorecardItems();
                return;
            },
            onCloseCard() {
                this.closeCard();
            },
            setScorecardCompletion() {
                const scorecardItemsTotal = (this.scorecardItems || []).length;
                if (scorecardItemsTotal === 0) {
                    return;
                }

                let scorecardItemsCompleted = 0;
                this.scorecardItems.forEach((x) => {
                    if (x.completed_at !== null) {
                        scorecardItemsCompleted++
                    }
                })

                const completion = (scorecardItemsCompleted / scorecardItemsTotal) * 100;

                this.handleUpdateScorecardCompletion(completion);
            },
            updateScorecardNotes(options) {
                if (!this.validScorecard) {
                    return;
                }

                this.isSavingScorecardNote = true;
                UpdateScorecard(this.scorecard.scorecard_id, options).then((response) => {
                    if (response?.Data?.scorecard?.scorecard_id > 0) {

                        // Only display snackbar when adding note to complete scorecard
                        if (this.readonly) {
                            this.showSnackbar("success", "Successfully updated scorecard notes");
                        }
                    } else {
                        this.showSnackbar("error", "Failed to update scorecard notes");
                    }

                    this.isSavingScorecardNote = false;
                });
            },
            updateScorecardItemAnswer(scorecardItem, answer) {
                if (!(scorecardItem?.scorecard_item_id > 0 && scorecardItem.scorecard_id > 0)) {
                    return;
                }

                const options = {
                    answer: answer,
                };

                // this.isSaving = true;
                UpdateScorecardItemAnswer(scorecardItem.scorecard_id, scorecardItem.scorecard_item_id, options).then((response) => {
                    if (response?.Data?.scorecard_item?.scorecard_item_id > 0) {
                        const scorecardItem = response.Data.scorecard_item;
                        this.handleUpdateScorecardItem(scorecardItem);
                        this.handleUpdateScorecard(scorecardItem.scorecard_id);
                    }

                    // this.isSaving = false;
                });
            },
            updateScorecardItemNotes(scorecardItem, options) {
                if (!(scorecardItem?.scorecard_item_id > 0 && scorecardItem.scorecard_id > 0)) {
                    return;
                }

                // set for saving ui on note - set to false by server response
                this.handleIsSavingScorecardItemNote(scorecardItem, true);

                UpdateScorecardItemNotes(scorecardItem.scorecard_id, scorecardItem.scorecard_item_id, options).then((response) => {
                    if (response?.Data?.scorecard_item?.scorecard_item_id > 0) {
                        const scorecardItem = response.Data.scorecard_item;
                        this.handleUpdateScorecardItem(scorecardItem);

                        // Only display snackbar when adding note to complete scorecard
                        if (this.readonly) {
                            this.showSnackbar("success", "Successfully updated scorecard item notes");
                        }
                    } else {
                        this.showSnackbar("error", "Failed to update scorecard item notes");
                    }

                    this.handleIsSavingScorecardItemNote(scorecardItem, false);
                });
            },
        },
        computed: {
            readonly() {
                return this.scorecard?.status === "complete" || this.scorecard?.status === "report_card" || this.isViewOnly;
            },
            validScorecard() {
                return this.scorecard?.scorecard_id > 0;
            },
            validScorecardItems() {
                return this.scorecardItems?.length > 0;
            },
        },
        components: {
            ScorecardItemSlider,
            ScorecardItemNotes,
            ScorecardNotes,
        },
        props: {
            closeCard: Function,
            handleUpdateScorecard: Function,
            handleUpdateScorecardCompletion: Function,
            isViewOnly: Boolean,
            showPrivateNotes: Boolean,
            scorecard: Object,
            showSnackbar: Function,
        },
        created() {
            this.init();
        },
    });
</script>

<style lang="scss" scoped>
    #app {
    }
</style>
